import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { themes } from '../design-system/themes';
import { usePreferences } from '../../shared/hooks/usePreferences';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { get } = usePreferences();
  
  // Récupérer le thème des préférences utilisateur
  const preferenceTheme = get('theme');
  
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Utiliser le thème des préférences si disponible
    if (preferenceTheme === 'light' || preferenceTheme === 'dark') {
      return preferenceTheme;
    }
    
    // Sinon, utiliser le thème sauvegardé dans le localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    
    // Si "system" ou aucun thème défini, utiliser la préférence système
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  
  const theme = themes[currentTheme] || themes.light;

  const updateDocumentClasses = useCallback((themeName) => {
    try {
      document.documentElement.classList.remove('dark', 'light');
      document.documentElement.classList.add(themeName);
    } catch (error) {
      console.error('Error updating document classes:', error);
    }
  }, []);

  const setTheme = useCallback((newTheme) => {
    try {
      if (!newTheme || (newTheme !== 'light' && newTheme !== 'dark')) {
        console.warn(`Theme '${newTheme}' is not valid. Using 'light' instead.`);
        newTheme = 'light';
      }
      console.log('ThemeProvider: Setting theme to', newTheme);
      setCurrentTheme(newTheme);
      localStorage.setItem('theme', newTheme);
      updateDocumentClasses(newTheme);
    } catch (error) {
      console.error('Error setting theme:', error);
    }
  }, [updateDocumentClasses]);

  const toggleTheme = useCallback(() => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [currentTheme, setTheme]);

  // Appliquer le thème quand les préférences changent
  useEffect(() => {
    if (preferenceTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      updateDocumentClasses(systemTheme);
      setCurrentTheme(systemTheme);
    } else if (preferenceTheme === 'light' || preferenceTheme === 'dark') {
      updateDocumentClasses(preferenceTheme);
      setCurrentTheme(preferenceTheme);
    }
  }, [preferenceTheme, updateDocumentClasses]);

  // Appliquer la classe initiale au chargement du composant
  useEffect(() => {
    updateDocumentClasses(currentTheme);
  }, [currentTheme, updateDocumentClasses]);

  return (
    <ThemeContext.Provider value={{ theme, currentTheme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}; 